// One realtime subscription per conversation the user is a member of
// (cached from list_my_conversations + a tiny conversation_members
// listener to catch brand-new threads). Turns inbound messages into:
//   * a Sonner toast (unless the user is already viewing that thread)
//   * an OS-level browser notification (if opted in + page not in focus)
//   * a short chime (if opted in)
//   * an unread count in the browser-tab title
//
// Previously this hook used a single unfiltered postgres_changes
// subscription on public.messages. The audit raised the question of
// whether Realtime's RLS-on-CDC reliably scopes that stream to rows the
// user can SELECT — even if it does, filtered per-conversation channels
// are strictly safer (smaller payloads, no dependence on RLS at the CDC
// layer, no cross-tenant data passing through this user's WebSocket).
// We now subscribe with `filter: conversation_id=eq.{id}` per active
// thread and refresh the channel set when the conversation list polls
// or a new membership row lands.

import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  conversationsQueryKey,
  useConversations,
} from '@/hooks/messaging/useConversations';
import {
  conversationMessagesQueryKey,
} from '@/hooks/messaging/useConversation';
import { unreadSummaryQueryKey } from '@/hooks/messaging/useUnreadSummary';
import { stripMentionMarkup } from '@/lib/message-mentions';
import {
  fireBrowserNotification,
  playMessageChime,
  prewarmMessageChime,
  resetTitle,
  setUnreadInTitle,
} from '@/lib/messaging/in-app-notifications';
import {
  conversationDisplayTitle,
  type ConversationListEntry,
} from '@/types/messaging';

const MAX_TRACKED_CONVERSATIONS = 50;

type MessageInsertPayload = {
  new: {
    id: string;
    conversation_id: string;
    sender_id: string;
    body: string | null;
    created_at: string;
    deleted_at: string | null;
  };
};

const senderNameCache = new Map<string, string>();

const lookupSenderName = async (senderId: string): Promise<string> => {
  const cached = senderNameCache.get(senderId);
  if (cached) return cached;
  const { data } = await supabase
    .from('profiles')
    .select('first_name, last_name, email')
    .eq('id', senderId)
    .maybeSingle();
  const name =
    (data && (`${data.first_name ?? ''} ${data.last_name ?? ''}`.trim() || data.email)) || 'Someone';
  senderNameCache.set(senderId, name);
  return name;
};

export const useGlobalMessageNotifications = () => {
  const { profile } = useAuth();
  const profileId = profile?.id ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  const qc = useQueryClient();

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  // Pre-render the chime WAV blob once the user is logged in so the
  // very first incoming message doesn't pay the OfflineAudioContext
  // render latency. Idempotent + non-blocking.
  useEffect(() => {
    if (!profileId) return;
    prewarmMessageChime();
  }, [profileId]);

  // Source the conversation ids we care about from the cached/polled
  // list_my_conversations data so we don't re-fetch separately.
  const { data: conversations } = useConversations();
  const trackedIds = useMemo(() => {
    const list = (conversations ?? []).filter((c) => !c.archived_at);
    // Most-recent first; cap to keep channel count bounded.
    return list
      .slice()
      .sort((a, b) => b.last_message_at.localeCompare(a.last_message_at))
      .slice(0, MAX_TRACKED_CONVERSATIONS)
      .map((c) => c.conversation_id);
  }, [conversations]);

  // Stable key for the channel set — only re-subscribes when the
  // membership of trackedIds changes (not on every render).
  const idsKey = useMemo(() => trackedIds.slice().sort().join(','), [trackedIds]);

  // Keep the tab title in sync with the cached unread total.
  const cachedUnread = qc.getQueryData(unreadSummaryQueryKey) as
    | { unread_count: number }[]
    | undefined;
  const totalUnread = useMemo(
    () => (cachedUnread ?? []).reduce((acc, r) => acc + (r.unread_count || 0), 0),
    [cachedUnread],
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    setUnreadInTitle(totalUnread);
    return () => resetTitle();
  }, [totalUnread]);

  const handleNewMessage = useMemo(
    () => async (msg: MessageInsertPayload['new']) => {
      if (!msg || !msg.id || !profileId) return;
      if (msg.sender_id === profileId) return;
      if (msg.deleted_at) return;

      const isFocusedOnThisThread =
        locationRef.current === `/messages/${msg.conversation_id}` ||
        locationRef.current === `/limited/messages/${msg.conversation_id}`;

      // Whether the user can actually SEE this tab right now. If
      // they've switched to a different browser tab (or minimised
      // the window), document.visibilityState is 'hidden' and
      // document.hasFocus() is false. Vanessa's report: when she
      // had the messages page open in one tab and was on another
      // tab, no chime / browser notification fired. The old check
      // assumed "is on this conversation page" === "is actively
      // looking at it" — which silenced cross-tab alerts entirely.
      const tabIsActive =
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible' &&
        document.hasFocus();

      qc.invalidateQueries({ queryKey: conversationsQueryKey });
      qc.invalidateQueries({ queryKey: unreadSummaryQueryKey });
      qc.invalidateQueries({
        queryKey: conversationMessagesQueryKey(msg.conversation_id),
      });

      // Suppress only when the user can actually see the conversation
      // (active tab AND on this thread's URL). Background tabs always
      // get notified — the chime + browser pop-up are how Vanessa
      // finds out about a new message without flipping back.
      if (isFocusedOnThisThread && tabIsActive) return;

      const list = (qc.getQueryData(conversationsQueryKey) as
        | ConversationListEntry[]
        | undefined) ?? [];
      const conv = list.find((c) => c.conversation_id === msg.conversation_id);
      const senderName = await lookupSenderName(msg.sender_id);
      const threadLabel = conv ? conversationDisplayTitle(conv) : 'New message';
      const showThreadInTitle = conv?.type === 'group' || conv?.type === 'broadcast';
      const title = showThreadInTitle ? threadLabel : senderName;
      const preview = stripMentionMarkup(msg.body ?? '').trim();
      const truncated = preview.length > 140 ? `${preview.slice(0, 137)}…` : preview;
      const bodyText = showThreadInTitle
        ? `${senderName}: ${truncated || 'sent a message'}`
        : truncated || 'sent you a message';

      toast.message(title, {
        id: `msg:${msg.conversation_id}`, // dedupes rapid bursts in the same thread
        description: bodyText,
        duration: 6000,
        action: {
          label: 'Open',
          onClick: () => navigate(`/messages/${msg.conversation_id}?msg=${msg.id}`),
        },
      });

      fireBrowserNotification(
        {
          title,
          body: bodyText,
          conversationId: msg.conversation_id,
          messageId: msg.id,
        },
        () => navigate(`/messages/${msg.conversation_id}?msg=${msg.id}`),
      );

      playMessageChime();
    },
    [profileId, navigate, qc],
  );

  // Per-conversation message subscriptions (filtered).
  useEffect(() => {
    if (!profileId || trackedIds.length === 0) return;

    const channels = trackedIds.map((id) =>
      supabase
        .channel(`messaging-conv-${id}-${profileId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${id}`,
          },
          (raw) => {
            const payload = raw as unknown as MessageInsertPayload;
            void handleNewMessage(payload.new);
          },
        )
        .subscribe(),
    );

    return () => {
      for (const ch of channels) {
        try {
          supabase.removeChannel(ch);
        } catch {
          /* no-op */
        }
      }
    };
  }, [idsKey, profileId, handleNewMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Membership listener — when a new conversation_members row lands for
  // this user, refresh the conversation list so the subscription set
  // picks up the new thread on the next render.
  useEffect(() => {
    if (!profileId) return;
    const channel = supabase
      .channel(`messaging-membership-${profileId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversation_members',
          filter: `profile_id=eq.${profileId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: conversationsQueryKey });
          qc.invalidateQueries({ queryKey: unreadSummaryQueryKey });
        },
      )
      .subscribe();
    return () => {
      try {
        supabase.removeChannel(channel);
      } catch {
        /* no-op */
      }
    };
  }, [profileId, qc]);
};
